import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Badge,
  Card,
  CardBody,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Link,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FaArrowRight, FaClipboardList, FaPoll } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useUser } from "../../contexts/UserContext";
import { getOrganizationSurveys } from "./organizationSurveysApi";
import TourModeActionButton from "./TourModeActionButton";
import { formatOrganizationSurveyTitle } from "../../utils/organizationSurveyHelper";

const getEmailLocalPart = (email) => {
  if (typeof email !== "string") return "";
  return email.split("@")[0]?.trim() || "";
};

const getWelcomeName = (user) =>
  user?.first_name?.trim() || getEmailLocalPart(user?.email);

const OrganizationDashboardPage = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const welcomeName = getWelcomeName(user);
  const isTour = user?.organization?.tour_mode === true;
  const [recentSurveys, setRecentSurveys] = useState([]);
  const [loadingRecentSurveys, setLoadingRecentSurveys] = useState(true);
  const [recentSurveysError, setRecentSurveysError] = useState("");

  useEffect(() => {
    let active = true;

    const loadRecentSurveys = async () => {
      try {
        setLoadingRecentSurveys(true);
        setRecentSurveysError("");
        const surveys = await getOrganizationSurveys();
        if (!active) return;
        setRecentSurveys(surveys.slice(0, 3));
      } catch (err) {
        if (!active) return;
        setRecentSurveysError(
          t("organization.dashboard_recent_surveys_error", "Could not load recent surveys.")
        );
      } finally {
        if (active) setLoadingRecentSurveys(false);
      }
    };

    loadRecentSurveys();

    return () => {
      active = false;
    };
  }, [t]);

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <HStack spacing={3} flexWrap="wrap">
          <Heading size="lg">
            {t("organization.dashboard_title", "Dashboard")}
          </Heading>
          {isTour ? <Badge colorScheme="blue">Tour Mode</Badge> : null}
        </HStack>
        <Text color="gray.600" mt={2}>
          {t(
            "organization.dashboard_description",
            "Start from the dashboard, then move into workbook and future school features from a single app-native home."
          )}
        </Text>
      </Box>

      <Card opacity={isTour ? 0.92 : 1}>
        <CardBody>
          <VStack align="start" spacing={3}>
            <Text fontSize="sm" fontWeight="semibold" textTransform="uppercase" color="gray.500">
              {t("organization.dashboard_welcome_label", "Welcome")}
            </Text>
            <Heading size="md">
              {welcomeName
                ? t("organization.dashboard_welcome_named", "Welcome, {{name}}", {
                    name: welcomeName,
                  })
                : t("organization.dashboard_welcome", "Welcome")}
            </Heading>
            <Text color="gray.600">
              {t(
                "organization.dashboard_welcome_copy",
                "This dashboard is the new entry point for school users inside the App shell."
              )}
            </Text>
          </VStack>
        </CardBody>
      </Card>

      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={5}>
        <GridItem>
          <Card h="100%" opacity={isTour ? 0.92 : 1}>
            <CardBody>
              <VStack align="start" spacing={4} h="100%">
                <HStack spacing={3}>
                  <Icon as={FaPoll} boxSize={5} color="teal.500" />
                  <Heading size="md">{t("organization.surveys", "Surveys")}</Heading>
                </HStack>
                <Text color="gray.600" flex="1">
                  {t(
                    "organization.dashboard_surveys_copy",
                    "Browse survey cycles directly, then move into workbook or the full results view."
                  )}
                </Text>
                <TourModeActionButton
                  as={RouterLink}
                  to="/app/organization/surveys"
                  colorScheme="teal"
                  variant="outline"
                  isTour={isTour}
                  rightIcon={<FaArrowRight />}
                >
                  {t("organization.open_surveys", "Open Surveys")}
                </TourModeActionButton>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card h="100%" opacity={isTour ? 0.92 : 1}>
            <CardBody>
              <VStack align="start" spacing={4} h="100%">
                <HStack spacing={3}>
                  <Icon as={FaClipboardList} boxSize={5} color="blue.500" />
                  <Heading size="md">{t("organization.workbook.title", "Workbook")}</Heading>
                </HStack>
                <Text color="gray.600" flex="1">
                  {t(
                    "organization.dashboard_workbook_copy",
                    "Review your latest survey cycle and move into strengths and development priorities."
                  )}
                </Text>
                <TourModeActionButton
                  as={RouterLink}
                  to="/app/organization/workbook"
                  colorScheme="blue"
                  variant="outline"
                  isTour={isTour}
                  rightIcon={<FaArrowRight />}
                >
                  {t("organization.open_workbook", "Open Workbook")}
                </TourModeActionButton>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      <Card opacity={isTour ? 0.92 : 1}>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Heading size="md">
              {t("organization.dashboard_recent_surveys", "Recent Surveys")}
            </Heading>

            {loadingRecentSurveys ? (
              <Box py={4} textAlign="center">
                <Spinner />
              </Box>
            ) : recentSurveysError ? (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {recentSurveysError}
              </Alert>
            ) : recentSurveys.length === 0 ? (
              <Text color="gray.600">
                {t(
                  "organization.dashboard_recent_surveys_empty",
                  "Recent survey cycles will appear here once your school has them available."
                )}
              </Text>
            ) : (
              <VStack align="stretch" spacing={3}>
                {recentSurveys.map((survey) => (
                  <Box
                    key={survey.organization_survey_id}
                    borderWidth="1px"
                    borderRadius="lg"
                    p={4}
                  >
                    <HStack justify="space-between" align="start" spacing={4} flexWrap="wrap">
                      <Box>
                        <Text fontWeight="semibold">{formatOrganizationSurveyTitle(survey)}</Text>
                        <Text color="gray.600" fontSize="sm" mt={1}>
                          {t(
                            "organization.dashboard_recent_surveys_meta",
                            "{{respondents}} respondents • {{completionRate}}% complete",
                            {
                              respondents: survey.total_respondents ?? 0,
                              completionRate: survey.completion_rate ?? 0,
                            }
                          )}
                        </Text>
                      </Box>
                      <HStack spacing={4}>
                        <Link
                          as={RouterLink}
                          color="blue.600"
                          to={`/app/organization/results/${survey.organization_survey_id}`}
                          state={{
                            surveyTitle: formatOrganizationSurveyTitle(survey),
                            survey,
                          }}
                        >
                          {t("organization.view_results", "View Results")}
                        </Link>
                        <Link
                          as={RouterLink}
                          color="blue.600"
                          to="/app/organization/workbook"
                          state={{
                            organizationSurveyId: survey.organization_survey_id,
                          }}
                        >
                          {t("organization.open_workbook", "Open Workbook")}
                        </Link>
                      </HStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default OrganizationDashboardPage;
